import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    PlaceCollectionContract,
    PlaceContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PlaceResourceService {
    private baseUrl: string;
    private searchBaseUrl: string;
    private placeUrl: string;
    private poiTypes: string[] = [
        'Unknown',
        'Lodging',
        'Fuel',
        'Food',
        'Medical',
    ];

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = configService.apiUrl.rideCommand;
        this.searchBaseUrl = configService.apiUrl.search;
        this.placeUrl = routingHelpersService.combineUrl(this.baseUrl, 'place');
    }

    getById(id: string): Observable<PlaceContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'place',
            id
        );
        let params = new HttpParams();
        params = params.set('skipAuthorization', 'true');
        return this.http.get<PlaceContract>(url, { params });
    }

    /**
     * @description Get places by bounds, including both standard and premium results. This endpoint currently has a bug in that it
     * may return premium places as standard.
     * @param lat - Center latitude
     * @param lng - Center longitude
     * @param radius - Radius of area to retrieve places in
     * @param maxResults - Maximum places to return
     * @param zoom - Zoom level, used to filter results
     * @param includePremium
     * @param includeStandard
     * @param excludedTypes
     */
    getByBounds(
        lat: number,
        lng: number,
        radius: number,
        maxResults: number,
        zoom?: number,
        includePremium = false,
        includeStandard = true,
        excludedTypes: string[] = []
    ): Observable<PlaceCollectionContract> {
        const url = this.routingHelpersService.combineUrl(
            this.searchBaseUrl,
            'v1',
            'place'
        );

        const types = this.poiTypes.filter((type) => {
            if (excludedTypes.length) {
                return !excludedTypes.includes(type);
            }
            return true;
        });

        includeStandard = includeStandard && Boolean(types.length);

        if (!includePremium && !includeStandard) {
            return of({
                features: [] as PlaceContract[],
            } as PlaceCollectionContract);
        }

        let params = new HttpParams()
            .set('Latitude', String(lat))
            .set('Longitude', String(lng))
            .set('MetersRadius', String(radius))
            .set('skipAuthorization', 'true');

        if (types.length) {
            params = params.set('PoiTypes', types.join(','));
        }

        if (!includePremium) {
            params = params.set('ExcludePremium', true);
        } else if (!includeStandard) {
            params = params.set('PremiumOnly', true);
        }
        if (typeof maxResults === 'number' && !Number.isNaN(maxResults)) {
            params = params.set('MaxStandardResults', String(maxResults));
        }
        if (zoom) {
            params = params.set('Zoom', zoom.toFixed(2));
        }

        return this.http.get<PlaceCollectionContract>(url, {
            params,
        });
    }

    /**
     * @description Get all premium places.
     * @param includeInactive - Include inactive sponsors in results
     * @param maxResults
     */
    getAllPremiumPlaces(
        includeInactive: boolean,
        maxResults: number | string
    ): Observable<PlaceCollectionContract> {
        const url = this.routingHelpersService.combineUrl(
            this.searchBaseUrl,
            'v1',
            'place'
        );
        let params = new HttpParams()
            .set('IncludeInactive', String(includeInactive))
            .set('PremiumOnly', true)
            .set('skipAuthorization', 'true');
        if (typeof maxResults === 'number') {
            params = params.set('MaxStandardResults', maxResults);
        }

        return this.http.get<PlaceCollectionContract>(url, { params });
    }

    /**
     * Get sponsors for the logged in user's club(s). Requires authorization to match user to club(s).
     * @param maxResults
     */
    getSponsorsForClub(
        maxResults: number | string
    ): Observable<PlaceCollectionContract> {
        const url = this.routingHelpersService.combineUrl(
            this.searchBaseUrl,
            'v1',
            'place'
        );
        let params = new HttpParams()
            .set('IncludeInactive', true)
            .set('IncludeExpired', true)
            .set('OwnedByMe', true)
            .set('PremiumOnly', true);
        if (typeof maxResults === 'number') {
            params = params.set('MaxStandardResults', maxResults);
        }
        const headers = new HttpHeaders().set(
            'Accept',
            'application/vnd.geo+json'
        );

        return this.http.get<PlaceCollectionContract>(url, {
            headers,
            params,
        });
    }

    deleteSponsor(id: string): Observable<void> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'place',
            'sponsor',
            id
        );
        let headers = new HttpHeaders();
        headers = headers.delete('Content-Type');

        return this.http.delete<undefined>(url, { headers });
    }

    createSponsor(sponsor: PlaceContract): Observable<PlaceContract> {
        return this.http.post<PlaceContract>(this.placeUrl, sponsor);
    }

    updateSponsor(sponsor: PlaceContract): Observable<PlaceContract> {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/vnd.geo+json');

        return this.http.put<PlaceContract>(this.placeUrl, sponsor, {
            headers,
        });
    }
}
